import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { PRODUCTS } from "@/data/content";
import { scrollToId } from "@/lib/api";
import { Eyebrow, Reveal } from "./Reveal";

const pick = (name) => {
  window.dispatchEvent(new CustomEvent("a613:product", { detail: name }));
  scrollToId("apply");
};

const byId = Object.fromEntries(PRODUCTS.map((p) => [p.id, p]));

const Cta = ({ p, light }) => (
  <button
    onClick={() => pick(p.name)}
    data-testid={`product-apply-${p.id}`}
    className={`group/cta inline-flex items-center gap-2 text-sm font-semibold ${light ? "text-white" : "text-[var(--ink)]"}`}
  >
    Apply for {p.short}
    <span className={`flex h-8 w-8 items-center justify-center rounded-full transition-[background-color,transform] duration-300 group-hover/cta:rotate-45 ${light ? "bg-white text-[var(--ink)]" : "bg-[var(--ink)] text-white group-hover/cta:bg-[var(--blue)]"}`}>
      <ArrowUpRight className="h-4 w-4" />
    </span>
  </button>
);

const Tag = ({ children, light }) => (
  <span className={`inline-block w-fit self-start rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] ${light ? "bg-white/15 text-white backdrop-blur" : "bg-[var(--green)]/10 text-[var(--green)]"}`}>{children}</span>
);

const PhotoCard = ({ p, className, big }) => (
  <motion.article
    whileHover="hover"
    data-testid={`product-card-${p.id}`}
    className={`group relative flex min-h-[340px] flex-col justify-end overflow-hidden rounded-[28px] bg-[var(--ink)] p-6 text-white sm:p-8 ${className}`}
  >
    <motion.img variants={{ hover: { scale: 1.06 } }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} src={p.img} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)] via-[var(--ink)]/60 to-[var(--ink)]/10" />
    <div className="relative">
      <Tag light>{p.tag}</Tag>
      <h3 className={`mt-4 font-display font-black italic tracking-tight ${big ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>{p.name}</h3>
      <p className="mt-3 max-w-md text-sm leading-relaxed text-white/85 sm:text-base">{p.desc}</p>
      {p.points && (
        <ul className="mt-5 space-y-2">
          {p.points.map((x) => (
            <li key={x} className="flex items-center gap-2 text-sm text-white/90"><Check className="h-4 w-4 text-[var(--lime)]" />{x}</li>
          ))}
        </ul>
      )}
      <div className="mt-6"><Cta p={p} light /></div>
    </div>
  </motion.article>
);

const PlainCard = ({ p, i }) => (
  <article data-testid={`product-card-${p.id}`} className="group relative flex min-h-[280px] flex-col justify-between overflow-hidden rounded-[28px] border border-[var(--line)] bg-white p-6 transition-[box-shadow,transform] duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(6,26,61,0.35)] sm:p-8">
    <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br from-[var(--sky)]/25 to-[var(--lime)]/25 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
    <div className="relative flex items-start justify-between">
      <Tag>{p.tag}</Tag>
      <span className="font-mono text-sm text-[var(--ink)]/40">0{i}</span>
    </div>
    <div className="relative">
      <h3 className="font-display text-2xl font-black italic tracking-tight">{p.name}</h3>
      <p className="mt-3 text-sm leading-relaxed text-[var(--ink)]/70 sm:text-base">{p.desc}</p>
      <div className="mt-6"><Cta p={p} /></div>
    </div>
  </article>
);

export const Products = () => (
  <section id="products" data-testid="products-section" className="relative py-24 lg:py-32">
    <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
      <div className="grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-7">
          <Eyebrow>Funding solutions</Eyebrow>
          <h2 className="mt-5 font-display text-4xl font-black italic leading-[1] tracking-tight sm:text-5xl lg:text-6xl">
            Simple, flexible <span className="text-brand-gradient">capital.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <p className="text-base leading-relaxed text-[var(--ink)]/70 md:text-lg">
            Seven ways to fund your business, one application. Tell us what you need and we'll match you with the right product.
          </p>
        </Reveal>
      </div>

      <div className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-12">
        <Reveal className="md:col-span-7 md:row-span-2 [&>article]:h-full">
          <PhotoCard p={byId.mca} big className="h-full md:min-h-[700px]" />
        </Reveal>
        <Reveal delay={0.08} className="md:col-span-5 [&>article]:h-full"><PhotoCard p={byId.heloc} className="h-full" /></Reveal>
        <Reveal delay={0.16} className="md:col-span-5 [&>article]:h-full"><PhotoCard p={byId.equipment} className="h-full" /></Reveal>
        {["loc", "sba", "term"].map((id, i) => (
          <Reveal key={id} delay={i * 0.08} className="md:col-span-4 [&>article]:h-full"><PlainCard p={byId[id]} i={i + 4} /></Reveal>
        ))}
        <Reveal className="md:col-span-12">
          <article data-testid="product-card-factoring" className="relative grid min-h-[320px] grid-cols-1 overflow-hidden rounded-[28px] bg-[var(--ink)] text-white md:grid-cols-2">
            <div className="relative z-10 flex flex-col justify-center p-6 sm:p-10">
              <Tag light>{byId.factoring.tag}</Tag>
              <h3 className="mt-4 font-display text-3xl font-black italic tracking-tight sm:text-4xl">{byId.factoring.name}</h3>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-white/85 sm:text-base">{byId.factoring.desc}</p>
              <div className="mt-6"><Cta p={byId.factoring} light /></div>
            </div>
            <div className="relative min-h-[240px] md:[clip-path:polygon(14%_0,100%_0,100%_100%,0_100%)]">
              <img src={byId.factoring.img} alt="Business owner managing invoices" className="absolute inset-0 h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-[var(--blue)]/40 to-transparent mix-blend-multiply" />
            </div>
          </article>
        </Reveal>
      </div>
    </div>
  </section>
);
